// Pull request diffs: GitHub's per-file patches parsed into hunks and lines, highlighted
// with highlight.js (loaded on first use, see hljs.js).

let highlighter = null

/** highlight.js with the languages registered in hljs.js, loaded once. */
export function loadHighlighter() {
  highlighter ??= import('./hljs.js').then(mod => mod.default)
  return highlighter
}

/**
 * Hunks of a unified diff: { header, lines: [{ type, old, new, text }] } with type
 * add, del, context or note ("\ No newline at end of file").
 */
export function parsePatch(patch) {
  const hunks = []
  let hunk = null
  let oldLine = 0
  let newLine = 0
  for (const line of patch.split('\n')) {
    const header = /^@@ -(\d+)(?:,\d+)? \+(\d+)(?:,\d+)? @@/.exec(line)
    if (header) {
      oldLine = Number(header[1])
      newLine = Number(header[2])
      hunk = { header: line, lines: [] }
      hunks.push(hunk)
    } else if (hunk && line) {
      const text = line.slice(1)
      if (line[0] === '+')
        hunk.lines.push({ type: 'add', new: newLine++, text })
      else if (line[0] === '-')
        hunk.lines.push({ type: 'del', old: oldLine++, text })
      else if (line[0] === '\\')
        hunk.lines.push({ type: 'note', text: line.slice(2) })
      else
        hunk.lines.push({
          type: 'context',
          old: oldLine++,
          new: newLine++,
          text,
        })
    }
  }
  return hunks
}

/**
 * Highlighted HTML split into lines: spans that run across a line break (comments,
 * strings) are closed at its end and opened again on the next line.
 */
export function splitLines(html) {
  const lines = []
  const openSpans = []
  let line = ''
  for (const part of html.split(/(<span[^>]*>|<\/span>|\n)/)) {
    if (part === '\n') {
      lines.push(line + '</span>'.repeat(openSpans.length))
      line = openSpans.join('')
    } else {
      if (part.startsWith('<span')) openSpans.push(part)
      else if (part === '</span>') openSpans.pop()
      line += part
    }
  }
  lines.push(line + '</span>'.repeat(openSpans.length))
  return lines
}

/** The highlight.js language for a file name, from its extension; null if unknown. */
export function languageOf(filename, hljs) {
  const name = filename.split('/').pop().toLowerCase()
  const ext = name.includes('.') ? name.split('.').pop() : name
  return hljs.getLanguage(ext) ? ext : null
}

/**
 * Sets `html` on the lines of a file's hunks. Each hunk's old and new side is
 * highlighted as a whole, so constructs over several lines keep their colors.
 */
export function highlightHunks(hunks, language, hljs) {
  const highlight = lines =>
    splitLines(
      hljs.highlight(lines.map(l => l.text).join('\n'), {
        language,
        ignoreIllegals: true,
      }).value
    )
  for (const hunk of hunks) {
    const newSide = highlight(
      hunk.lines.filter(l => l.type !== 'del' && l.type !== 'note')
    )
    const oldSide = highlight(
      hunk.lines.filter(l => l.type !== 'add' && l.type !== 'note')
    )
    let n = 0
    let o = 0
    for (const line of hunk.lines) {
      if (line.type === 'add') line.html = newSide[n++]
      else if (line.type === 'del') line.html = oldSide[o++]
      else if (line.type === 'context') {
        line.html = newSide[n++]
        o++
      }
    }
  }
}

/**
 * Sets `commit` on added lines from blame ranges ([from, to, sha] over the file at the
 * PR's head), for commits in `shas`: the PR's own, lines from before it stay plain.
 */
export function annotateHunks(hunks, ranges, shas) {
  for (const hunk of hunks)
    for (const line of hunk.lines) {
      if (line.type !== 'add') continue
      const range = ranges.find(
        ([from, to]) => line.new >= from && line.new <= to
      )
      if (range && shas.has(range[2])) line.commit = range[2]
    }
}

/**
 * Sets `commit` on deleted lines: the first PR commit (oldest first) whose own patch of
 * the file deleted a line with the same text. `commitHunks`: [{ sha, hunks }]. Exact for
 * typical PRs; identical lines (a lone `}`) can land on another commit that deleted one.
 */
export function annotateDeletions(hunks, commitHunks) {
  const deleted = commitHunks.map(({ sha, hunks: own }) => ({
    sha,
    texts: own.flatMap(h =>
      h.lines.filter(l => l.type === 'del').map(l => l.text)
    ),
  }))
  for (const hunk of hunks)
    for (const line of hunk.lines) {
      if (line.type !== 'del') continue
      for (const { sha, texts } of deleted) {
        const i = texts.indexOf(line.text)
        if (i < 0) continue
        texts.splice(i, 1)
        line.commit = sha
        break
      }
    }
}

/**
 * Rows the commit cell of each line spans: the length of its run of lines with the same
 * commit (or none) on the first line, 0 on the others.
 */
export function commitRuns(lines) {
  const spans = lines.map(() => 0)
  let start = 0
  for (let i = 1; i <= lines.length; i++)
    if (i === lines.length || lines[i].commit !== lines[start].commit) {
      spans[start] = i - start
      start = i
    }
  return spans
}

/** Sorts paths like GitHub's file tree: in each folder, its subfolders before its files. */
export function byTreeOrder(a, b) {
  const as = a.split('/')
  const bs = b.split('/')
  const i = as.findIndex((part, j) => part !== bs[j])
  if (i < 0) return as.length - bs.length
  const aIsFolder = i < as.length - 1
  const bIsFolder = i < bs.length - 1
  if (aIsFolder !== bIsFolder) return aIsFolder ? -1 : 1
  return as[i] < bs[i] ? -1 : 1
}

/**
 * Review threads by file and line: path → `${side}:${line}` → threads. Outdated
 * threads (no line, it changed since) are left out, like on GitHub's diff.
 */
export function threadsByLine(threads) {
  const files = new Map()
  for (const thread of threads) {
    if (thread.line == null) continue
    if (!files.has(thread.path)) files.set(thread.path, new Map())
    const lines = files.get(thread.path)
    const key = `${thread.side}:${thread.line}`
    lines.set(key, [...(lines.get(key) || []), thread])
  }
  return files
}

// a thread's side: LEFT counts the old lines, RIGHT the new ones
const THREAD_KEYS = {
  add: line => [`RIGHT:${line.new}`],
  del: line => [`LEFT:${line.old}`],
  context: line => [`LEFT:${line.old}`, `RIGHT:${line.new}`],
}

/** The review threads on a line of a file's diff (`byLine` from threadsByLine). */
export const lineThreads = (line, byLine) =>
  (THREAD_KEYS[line.type]?.(line) || []).flatMap(key => byLine?.get(key) || [])
